import PageComponent from "../components/page/PageComponent.vue";
import SectionSheet from "../components/section_sheet/SectionSheet.vue";
import PageControl from "../components/page_control/PageControl.vue";
import { format_date } from "@/utils/date_formatter";


export default {
  name: "IncomeEstimatorReport",
  components: { PageControl, PageComponent, SectionSheet },
  props: {
    sheet_elements: {
      type: String,
      default: '.sheet',
      required: false
    },
    income_data: {
      type: Object,
      default: () => ({}),
      required: false,
    },
  },
  data() {
    return {
      export_options: {
        income_summary: false,
        raw_json: false,
      },
      export_format: "csv",
      libraries_loaded: {
        papa_parse: false,
        sheetjs: false,
      },
    };
  },
  computed: {
    has_selected_tables() {
      return Object.values(this.export_options).some(option => option);
    },
    safe_income_data() {
      return this.income_data || {};
    },

    // Income data helpers
    status() {
      return this.safe_income_data.status || 'N/A';
    },

    result() {
      return this.safe_income_data.result || {};
    },

    predicted_income() {
      return this.result.predicted_income || 'N/A';
    },

    // Report metadata
    report_reference() {
      return 'INC-' + Date.now();
    },

    generation_date() {
      return new Date().toISOString();
    },

    formatted_generation_date() {
      return this.formatDate(this.generation_date);
    },

    modal_instance() {
      var modal = new window.bootstrap.Modal(document.getElementById(`export-modal-${this.report_reference}`))
      return modal;
    }
  },

  methods: {
    close_modal() {
      this.modal_instance.hide();
    },

    // Utility methods
    formatDate(date) {
      return format_date(date);
    },

    // Export functionality
    show_export_modal() {
      this.export_options.income_summary = true;
      this.export_options.raw_json = false;
      this.modal_instance.show();
    },

    async load_export_libraries() {
      if (!this.libraries_loaded.papa_parse) {
        await this.load_script('https://cdnjs.cloudflare.com/ajax/libs/PapaParse/5.4.1/papaparse.min.js');
        this.libraries_loaded.papa_parse = true;
      }

      if (!this.libraries_loaded.sheetjs) {
        await this.load_script('https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js');
        this.libraries_loaded.sheetjs = true;
      }
    },

    load_script(src) {
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },

    async export_data() {
      await this.load_export_libraries();

      if (this.export_format === 'csv') {
        await this.export_csv();
      } else if (this.export_format === 'excel') {
        await this.export_excel();
      } else if (this.export_format === 'json') {
        await this.export_json();
      }

      // Close modal
      this.close_modal();
    },

    // Prepare income summary data
    prepare_income_summary_data() {
      return [
        {
          "Report Reference": this.report_reference,
          "Status": this.status,
          "Predicted Income": this.predicted_income,
          "Generated Date": this.formatted_generation_date
        }
      ];
    },

    // Prepare raw JSON data
    prepare_raw_json_data() {
      return [this.safe_income_data];
    },

    // Validation method to test export data completeness
    validate_export_data() {
      // Implement validation logic if needed
      return true;
    },

    // Export methods
    async export_csv() {
      let data = [];

      if (this.export_options.income_summary) {
        data = data.concat(this.prepare_income_summary_data());
      }

      if (this.export_options.raw_json) {
        data = data.concat(this.prepare_raw_json_data());
      }

      const csv = window.Papa.unparse(data);
      this.download_file(csv, `income_estimator_report_${this.report_reference}.csv`, 'text/csv');
    },

    async export_excel() {
      const wb = window.XLSX.utils.book_new();

      if (this.export_options.income_summary) {
        const ws = window.XLSX.utils.json_to_sheet(this.prepare_income_summary_data());
        window.XLSX.utils.book_append_sheet(wb, ws, 'Income Summary');
      }

      if (this.export_options.raw_json) {
        const ws = window.XLSX.utils.json_to_sheet(this.prepare_raw_json_data());
        window.XLSX.utils.book_append_sheet(wb, ws, 'Raw JSON');
      }

      window.XLSX.writeFile(wb, `income_estimator_report_${this.report_reference}.xlsx`);
    },

    async export_json() {
      let data = {};

      if (this.export_options.income_summary) {
        data.income_summary = this.prepare_income_summary_data();
      }

      if (this.export_options.raw_json) {
        data.raw_json = this.prepare_raw_json_data();
      }

      const json = JSON.stringify(data, null, 2);
      this.download_file(json, `income_estimator_report_${this.report_reference}.json`, 'application/json');
    },

    download_file(content, filename, content_type) {
      const blob = new Blob([content], { type: content_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  },
};
